import { brand } from "../../config/theme";
import { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Header } from "../../components/base/Header";
import { AppsSidebar, useFilteredCategories } from "../../components/base/AppsSidebar";
import { useTheme } from "../../context/ThemeContext";
import { useUserStore } from "../../store/userStore";
import { useActiveAppStore } from "../../store/activeAppStore";
import { logoutUser } from "../../pages/login/functions";
import type { AppItem } from "../../components/base/AppsSidebar";
import Swal from "sweetalert2";
import { faGear, faUser } from "@fortawesome/free-solid-svg-icons";

// También sirve a Configuración (/ajustes): misma estructura, otra app activa.
export default function ProfileLayout({ appId = "profile" }: { appId?: "profile" | "ajustes" }) {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const { themeMode, isDarkMode, setTheme } = useTheme();
  const user = useUserStore((state) => state.user);
  const filteredCategories = useFilteredCategories();
  const [isAppsSidebarOpen, setIsAppsSidebarOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const setActiveApp = useActiveAppStore((state) => state.setActiveApp);

  useEffect(() => {
    if (appId === "ajustes") setActiveApp("ajustes", t("apps.ajustes.name"), t("apps.ajustes.description"), faGear, "bg-slate-500");
    else
      setActiveApp(
        "profile",
        t("userProfile.myProfile", "My Profile"),
        t("userProfile.profileDescription", "Manage your account"),
        faUser,
        "bg-primary-500"
      );
  }, [setActiveApp, t, appId]);

  const openAppsSidebar = () => setIsAppsSidebarOpen(true);
  const closeAppsSidebar = () => setIsAppsSidebarOpen(false);
  const toggleMobileMenu = () => setIsMobileMenuOpen((prev) => !prev);

  const handleAppSelect = (app: AppItem) => {
    if (app.id === appId) {
      closeAppsSidebar();
      return;
    }

    if (app.isComingSoon) {
      Swal.fire({
        title: t("appsSidebar.comingSoon"),
        text: t("appsSidebar.comingSoonText", { name: app.name }),
        icon: "info",
        confirmButtonText: t("appsSidebar.understood"),
        confirmButtonColor: brand.primary,
      });
      return;
    }

    if (app.isLocked) {
      Swal.fire({
        title: t("appsSidebar.premiumFeature"),
        text: t("appsSidebar.premiumRequired", { name: app.name }),
        icon: "warning",
        confirmButtonText: t("appsSidebar.understood"),
        confirmButtonColor: brand.primary,
      });
      return;
    }

    if (app.route) {
      closeAppsSidebar();
      navigate(app.route);
    } else if (app.externalUrl) {
      window.open(app.externalUrl, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div className={`h-screen flex flex-col ${isDarkMode ? "bg-dark-bg" : "bg-gray-50"}`}>
      <Header
        onOpenAppsSidebar={openAppsSidebar}
        onToggleMobileMenu={toggleMobileMenu}
        isMobileMenuOpen={isMobileMenuOpen}
        isDarkMode={isDarkMode}
      />

      <div className="flex-1 min-h-0 overflow-hidden">
        <Outlet />
      </div>

      <AppsSidebar
        isOpen={isAppsSidebarOpen}
        onClose={closeAppsSidebar}
        categories={filteredCategories}
        selectedAppId={appId}
        onAppSelect={handleAppSelect}
        isDarkMode={isDarkMode}
        currentTheme={themeMode}
        onThemeChange={setTheme}
        isProfileActive={appId === "profile"}
        userInfo={user ? {
          first_name: user.first_name,
          last_name: user.last_name,
          email: user.email,
          username: user.username,
        } : undefined}
        onProfileClick={() => {
          closeAppsSidebar();
        }}
        onLogout={async () => {
          setIsLoggingOut(true);
          await logoutUser();
          setIsLoggingOut(false);
        }}
        isLoggingOut={isLoggingOut}
      />
    </div>
  );
}
